import { Component } from '@theme/component';

const MIN_POSITION = 0;
const MAX_POSITION = 100;
const KEY_STEP = 2;
const PAGE_STEP = 10;
const DRAG_THRESHOLD = 3;

/**
 * @typedef {Object} BeforeAfterSliderRefs
 * @property {HTMLElement} handle - Focusable slider handle (role="slider")
 */

/**
 * Before/after image comparison. The divider position lives in the
 * `--before-after-position` custom property; the handle mirrors it in ARIA.
 *
 * @extends {Component<BeforeAfterSliderRefs>}
 */
class BeforeAfterSlider extends Component {
  requiredRefs = ['handle'];

  /** @type {number | null} */
  #pointerId = null;

  #startX = 0;

  #hasMoved = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#resetPointer);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#resetPointer);
  }

  /**
   * @returns {number} Current divider position (0–100)
   */
  get position() {
    return Number(this.refs.handle.getAttribute('aria-valuenow')) || 50;
  }

  /**
   * @param {number} value - Divider position as a percentage from the left edge
   */
  setPosition(value) {
    const position = Math.min(MAX_POSITION, Math.max(MIN_POSITION, value));
    const rounded = Math.round(position);
    const { handle } = this.refs;

    this.style.setProperty('--before-after-position', `${position}%`);
    handle.setAttribute('aria-valuenow', String(rounded));
    handle.setAttribute('aria-valuetext', `${rounded}% before, ${MAX_POSITION - rounded}% after`);
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const steps = {
      ArrowLeft: -KEY_STEP,
      ArrowDown: -KEY_STEP,
      ArrowRight: KEY_STEP,
      ArrowUp: KEY_STEP,
      PageDown: -PAGE_STEP,
      PageUp: PAGE_STEP,
    };

    if (event.key === 'Home') {
      this.setPosition(MIN_POSITION);
    } else if (event.key === 'End') {
      this.setPosition(MAX_POSITION);
    } else if (event.key in steps) {
      this.setPosition(this.position + steps[/** @type {keyof typeof steps} */ (event.key)]);
    } else {
      return;
    }

    event.preventDefault();
  }

  /**
   * @param {PointerEvent} event
   * @returns {number}
   */
  #positionFromPointer(event) {
    const rect = this.getBoundingClientRect();

    if (!rect.width) return this.position;

    return ((event.clientX - rect.left) / rect.width) * MAX_POSITION;
  }

  /**
   * Touch input waits for horizontal movement so vertical page scrolls
   * (handed to the browser via `touch-action: pan-y`) don't jump the divider.
   *
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.button !== 0) return;

    this.#pointerId = event.pointerId;
    this.#startX = event.clientX;
    this.#hasMoved = false;

    if (event.pointerType !== 'mouse') return;

    event.preventDefault();
    this.setPointerCapture(event.pointerId);
    this.refs.handle.focus({ preventScroll: true });
    this.setPosition(this.#positionFromPointer(event));
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerId !== this.#pointerId) return;
    if (!this.#hasMoved && Math.abs(event.clientX - this.#startX) < DRAG_THRESHOLD) return;

    this.#hasMoved = true;
    this.setPosition(this.#positionFromPointer(event));
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerId !== this.#pointerId) return;

    if (!this.#hasMoved && event.pointerType !== 'mouse') {
      this.setPosition(this.#positionFromPointer(event));
    }

    this.#resetPointer();
  };

  #resetPointer = () => {
    this.#pointerId = null;
    this.#hasMoved = false;
  };
}

if (!customElements.get('before-after-slider')) {
  customElements.define('before-after-slider', BeforeAfterSlider);
}
